"use client";

import { useSyncExternalStore } from "react";

import {
  decideMerchantMarketingTags,
  PRIVACY_CHOICE_EVENT,
  readPrivacySignals,
  type MerchantMarketingTagsDecision,
  type PrivacySignals,
} from "./privacySignals";

/**
 * The four possible readings, as stable objects: `useSyncExternalStore` needs
 * a snapshot that is referentially equal while nothing changed.
 */
const SNAPSHOTS: Record<string, PrivacySignals> = {
  "00": { globalPrivacyControl: false, saleShareOptOut: false },
  "01": { globalPrivacyControl: false, saleShareOptOut: true },
  "10": { globalPrivacyControl: true, saleShareOptOut: false },
  "11": { globalPrivacyControl: true, saleShareOptOut: true },
};

function readSnapshot(): PrivacySignals {
  const signals = readPrivacySignals();
  return SNAPSHOTS[`${signals.globalPrivacyControl ? 1 : 0}${signals.saleShareOptOut ? 1 : 0}`];
}

function readServerSnapshot(): PrivacySignals | null {
  return null;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(PRIVACY_CHOICE_EVENT, onChange);
  return () => window.removeEventListener(PRIVACY_CHOICE_EVENT, onChange);
}

/**
 * The visitor's privacy signals, read on the client.
 *
 * `null` on the server render and while hydrating it: no visitor's browser has
 * been read there, and a component that loads a merchant tag must render
 * NOTHING until it has been. That is what keeps a tag (or the
 * `<link rel="preload">` React emits for a `next/script` src) out of the
 * server HTML, so a GPC browser never contacts the tag's host at all.
 *
 * Re-reads when a choice is recorded on this page ({@link PRIVACY_CHOICE_EVENT}).
 */
export function usePrivacySignals(): PrivacySignals | null {
  return useSyncExternalStore<PrivacySignals | null>(subscribe, readSnapshot, readServerSnapshot);
}

/**
 * {@link decideMerchantMarketingTags} for this visitor — `null` until the
 * browser has been read. Callers load a merchant tag only on `load === true`.
 */
export function useMerchantMarketingTagsDecision(): MerchantMarketingTagsDecision | null {
  const signals = usePrivacySignals();
  return signals ? decideMerchantMarketingTags(signals) : null;
}
